import { useState, useRef, useEffect } from "react";
import BorderBox from "./BorderBox.jsx";
import Pill from "./Pill.jsx";
import topics from "../data/topics.js";
import requestHandler from "../utils/requestHandler.js";
import FlagModal from "./FlagModal.jsx";

export default function PostCreator({ initialTopic = "", onPostCreated }) {
  const [postTitle, setPostTitle] = useState("");
  const [postContent, setPostContent] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const textareaRef = useRef(null);

  // Auto-resize textarea as user types
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const maxHeight = 300;
      if (textareaRef.current.scrollHeight > maxHeight) {
        textareaRef.current.style.height = maxHeight + "px";
      } else {
        textareaRef.current.style.height =
          textareaRef.current.scrollHeight + "px";
      }
    }
  }, [postContent]);

  // Pre-select topic when user starts typing if there's an initial topic
  useEffect(() => {
    if ((postTitle.trim() || postContent.trim()) && initialTopic) {
      setSelectedTopic(initialTopic);
    }
  }, [postTitle, postContent, initialTopic]);

  // Clear selected topic when initialTopic is cleared (user clicks back arrow)
  useEffect(() => {
    if (!initialTopic && !postTitle.trim() && !postContent.trim()) {
      setSelectedTopic("");
    }
  }, [initialTopic, postTitle, postContent]);

  // Expand when user starts typing
  useEffect(() => {
    if (postTitle.trim() && !isExpanded) {
      setIsExpanded(true);
    }
  }, [postTitle, isExpanded]);

  // Collapse when all fields are empty
  useEffect(() => {
    if (!postTitle.trim() && !postContent.trim() && !selectedTopic) {
      setIsExpanded(false);
    }
  }, [postTitle, postContent, selectedTopic]);

  const handlePost = async () => {
    if (postTitle.trim() && postContent.trim() && selectedTopic) {
      setIsSubmitting(true);
      const response = await requestHandler({
        path: "posts",
        method: "POST",
        body: {
          title: postTitle,
          content: postContent,
          topic: selectedTopic,
        },
      });

      if (response.success) {
        if (response.data.flagged === true) {
          setIsSubmitting(false);
          setShowFlagModal(true);
          return;
        }

        setPostTitle("");
        setPostContent("");
        setSelectedTopic("");
        setIsExpanded(false);
        if (onPostCreated) {
          onPostCreated(response.data);
        }
        window.location.reload();
      } else {
        console.error("Failed to create post:", response.error);
      }
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.ctrlKey) {
      handlePost();
    }
  };

  const isFormValid = postTitle.trim() && postContent.trim() && selectedTopic;

  return (
    <>
      <FlagModal
        isOpen={showFlagModal}
        onClose={() => setShowFlagModal(false)}
      />
      <BorderBox padding="p-6" className="h-fit">
      <div className="flex flex-col gap-4">
        <label htmlFor="post-title" className="sr-only">
          Post title
        </label>
        <input
          id="post-title"
          placeholder="Create post"
          value={postTitle}
          onChange={(e) => setPostTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full px-4 py-3 rounded-2xl border border-lz-paper-border dark:border-lz-charcoal-border bg-lz-concrete dark:bg-lz-ink text-black dark:text-lz-concrete placeholder-lz-ink-mid dark:placeholder-lz-concrete focus:outline-none focus:ring-2 focus:ring-lz-candle-dim transition-all duration-300"
        />

        <div
          className={`transition-all duration-300 ${
            isExpanded
              ? "max-h-[600px] opacity-100 overflow-visible"
              : "max-h-0 opacity-0 overflow-hidden"
          }`}
        >
          <div className="flex flex-col gap-4">
            <label htmlFor="post-body" className="sr-only">
              Post body
            </label>
            <textarea
              id="post-body"
              ref={textareaRef}
              placeholder="Post Body *"
              value={postContent}
              onChange={(e) => setPostContent(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full px-4 py-3 rounded-2xl border border-lz-paper-border dark:border-lz-charcoal-border bg-lz-concrete dark:bg-lz-ink text-black dark:text-lz-concrete placeholder-lz-ink-mid dark:placeholder-lz-concrete focus:outline-none focus:ring-2 focus:ring-lz-candle-dim resize-none overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/20 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.2)_transparent]"
              rows="1"
              style={{ minHeight: "100px" }}
            />

            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-lz-ink-mid dark:text-lz-concrete">
                  Topic *
                </span>
                {!selectedTopic && isExpanded && (
                  <span className="text-xs text-red-500">
                    Please select a topic
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {topics.map((topic) => (
                  <Pill
                    key={topic.index}
                    selected={selectedTopic === topic.index}
                    onClick={() => setSelectedTopic(topic.index)}
                    size="small"
                    textCase="title"
                  >
                    {topic.label}
                  </Pill>
                ))}
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-lz-ink-mid dark:text-lz-concrete">
                  * All fields are required
                </span>
                <button
                  onClick={handlePost}
                  disabled={!isFormValid || isSubmitting}
                  className={`px-6 py-2 border-2 rounded-full transition-colors ${
                    isFormValid && !isSubmitting
                      ? "bg-lz-ember dark:bg-lz-ink-mid dark:border-lz-candle-dim border-lz-ember hover:border-lz-ink-mid text-white hover:bg-lz-ink-mid dark:hover:bg-lz-candle-dim dark:hover:text-lz-ink-mid"
                      : "bg-gray-400 border-gray-400 text-gray-200 cursor-not-allowed opacity-50"
                  }`}
                >
                  {isSubmitting ? "Posting..." : "Post"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BorderBox>
    </>
  );
}
